import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingCart, Search, Heart, Menu, X, Home, Sun, Moon, MapPin, Loader2, RefreshCw, LogOut, Shield } from 'lucide-react';
import { useShoppingContext } from '@/context/ShoppingContext';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { useUserLocation } from '@/hooks/useUserLocation';
import { useAdmin } from '@/hooks/useAdmin';
import grozoLogo from '@/assets/grozo-logo-v2.png';

const navItems = [
  { to: '/', label: 'Home', icon: Home },
  { to: '/compare', label: 'Compare', icon: Search },
  { to: '/list', label: 'My List', icon: ShoppingCart },
  { to: '/saved', label: 'Saved', icon: Heart },
];

function useTheme() {
  const [dark, setDark] = useState(() => {
    if (typeof window === 'undefined') return false;
    const stored = localStorage.getItem('theme');
    if (stored) return stored === 'dark';
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    document.documentElement.classList.toggle('dark', dark);
    localStorage.setItem('theme', dark ? 'dark' : 'light');
  }, [dark]);

  return [dark, () => setDark(d => !d)] as const;
}

export default function AppNavbar() {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { items } = useShoppingContext();
  const [dark, toggleDark] = useTheme();
  const { user, signOut } = useAuth();
  const { isAdmin } = useAdmin();
  const { location: userLoc, loading: locLoading, error: locError, availableStores, detectLocation: refreshLoc } = useUserLocation();
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    supabase
      .from('profiles')
      .select('avatar_url')
      .eq('user_id', user.id)
      .single()
      .then(({ data }) => {
        if (data?.avatar_url) setAvatarUrl(data.avatar_url);
      });
  }, [user]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      <nav className={`sticky top-0 z-50 transition-all duration-300 ${scrolled ? 'glass-nav shadow-sm' : 'bg-transparent border-b border-transparent'}`}>
        <div className="container flex h-16 items-center justify-between">
          <Link to="/" className="flex flex-col font-display group">
            <div className="flex items-center gap-2 text-xl font-bold text-primary">
              <img
                src={grozoLogo}
                alt="Grozo logo"
                className="h-8 w-8 sm:h-9 sm:w-9 object-contain transition-transform duration-300 group-hover:scale-110"
              />
              <span className="hidden sm:inline tracking-tight">Grozo</span>
            </div>
            <div className="hidden sm:flex items-center gap-1 text-[11px] text-muted-foreground ml-[2.75rem]">
              {locLoading ? (
                <span className="flex items-center gap-1"><Loader2 className="h-2.5 w-2.5 animate-spin" /> Detecting...</span>
              ) : userLoc ? (
                <>
                  <MapPin className="h-2.5 w-2.5 text-primary/70" />
                  <span className="truncate max-w-[200px]" title={userLoc.fullAddress || userLoc.city}>
                    {userLoc.fullAddress || userLoc.city}
                  </span>
                  <span className="text-muted-foreground/50">·</span>
                  <span>{availableStores.length} stores</span>
                  <button
                    onClick={(e) => { e.preventDefault(); refreshLoc(); }}
                    aria-label="Refresh location"
                    className="text-primary/60 hover:text-primary transition-colors"
                  >
                    <RefreshCw className="h-2.5 w-2.5" />
                  </button>
                </>
              ) : (
                <>
                  <MapPin className="h-2.5 w-2.5 text-muted-foreground/50" />
                  <span>{locError || 'Location unavailable'}</span>
                </>
              )}
            </div>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <div className="flex items-center gap-0.5 rounded-2xl bg-muted/50 dark:bg-muted/30 p-1 border border-border/30">
              {navItems.map(item => {
                const active = location.pathname === item.to;
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={`relative flex items-center gap-1.5 rounded-xl px-3.5 py-2 text-sm font-medium transition-all duration-200 ${
                      active
                        ? 'bg-card text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <item.icon className="h-4 w-4" />
                    {item.label}
                    {item.label === 'My List' && items.length > 0 && (
                      <Badge className="ml-0.5 h-5 min-w-[20px] rounded-full bg-primary px-1.5 text-xs text-primary-foreground">
                        {items.length}
                      </Badge>
                    )}
                  </Link>
                );
              })}
            </div>

            {/* Dark mode toggle */}
            <button
              onClick={toggleDark}
              className="ml-2 flex h-9 w-9 items-center justify-center rounded-xl border border-border/40 bg-muted/40 text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-200"
              aria-label="Toggle dark mode"
            >
              {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>

            {/* Profile & Logout */}
            {user && (
              <div className="ml-1 flex items-center gap-1">
                {isAdmin && (
                  <Link
                    to="/admin"
                    className="flex h-9 items-center gap-1.5 rounded-xl border border-primary/30 bg-primary/10 px-3 text-xs font-semibold text-primary hover:bg-primary/15 transition-all"
                    title="Admin panel"
                  >
                    <Shield className="h-3.5 w-3.5" />
                    Admin
                  </Link>
                )}
                <Link
                  to="/profile"
                  className="flex h-9 w-9 items-center justify-center rounded-xl border border-border/40 bg-muted/40 hover:bg-muted transition-all duration-200"
                  aria-label="Profile"
                  title={user.email || 'Profile'}
                >
                  <Avatar className="h-7 w-7">
                    <AvatarImage src={avatarUrl || undefined} alt="Profile" />
                    <AvatarFallback className="text-xs font-semibold bg-primary/10 text-primary">
                      {user.email?.slice(0, 2).toUpperCase() || 'U'}
                    </AvatarFallback>
                  </Avatar>
                </Link>
                <button
                  onClick={signOut}
                  className="flex h-9 w-9 items-center justify-center rounded-xl border border-border/40 bg-muted/40 text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-all duration-200"
                  aria-label="Sign out"
                  title="Sign out"
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 md:hidden">
            <button
              onClick={toggleDark}
              className="p-2.5 rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
              aria-label="Toggle dark mode"
            >
              {dark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>
            <button
              className="p-2.5 rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="md:hidden glass-nav animate-fade-in">
            <div className="container py-3 space-y-1">
              {navItems.map(item => {
                const active = location.pathname === item.to;
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={() => setMobileOpen(false)}
                    className={`flex items-center gap-2.5 rounded-xl px-4 py-3 text-sm font-medium transition-all ${
                      active ? 'bg-accent text-accent-foreground' : 'text-muted-foreground hover:bg-muted/50'
                    }`}
                  >
                    <item.icon className="h-4 w-4" />
                    {item.label}
                    {item.label === 'My List' && items.length > 0 && (
                      <Badge className="ml-auto h-5 min-w-[20px] rounded-full bg-primary px-1.5 text-xs text-primary-foreground">
                        {items.length}
                      </Badge>
                    )}
                  </Link>
                );
              })}
              {user && (
                <>
                  <div className="border-t border-border/40 my-1" />
                  <Link
                    to="/profile"
                    onClick={() => setMobileOpen(false)}
                    className={`flex items-center gap-2.5 rounded-xl px-4 py-3 text-sm font-medium transition-all ${
                      location.pathname === '/profile' ? 'bg-accent text-accent-foreground' : 'text-muted-foreground hover:bg-muted/50'
                    }`}
                  >
                    <Avatar className="h-5 w-5">
                      <AvatarImage src={avatarUrl || undefined} alt="Profile" />
                      <AvatarFallback className="text-[9px] font-semibold bg-primary/10 text-primary">
                        {user.email?.slice(0, 2).toUpperCase() || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    Profile
                  </Link>
                  <button
                    onClick={() => { setMobileOpen(false); signOut(); }}
                    className="flex w-full items-center gap-2.5 rounded-xl px-4 py-3 text-sm font-medium text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-all"
                  >
                    <LogOut className="h-4 w-4" />
                    Sign out
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 glass-nav border-t border-border/40 pb-[env(safe-area-inset-bottom)]">
        <div className="flex items-center justify-around py-1.5">
          {navItems.map(item => {
            const active = location.pathname === item.to;
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`relative flex flex-col items-center gap-0.5 px-3 py-1.5 text-[10px] font-medium transition-all duration-200 ${
                  active ? 'text-primary scale-105' : 'text-muted-foreground'
                }`}
              >
                <item.icon className={`h-5 w-5 transition-all duration-200 ${active ? 'drop-shadow-[0_0_6px_hsl(var(--primary)/0.4)]' : ''}`} />
                {item.label}
                {item.label === 'My List' && items.length > 0 && (
                  <span className="absolute -top-0.5 right-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary px-1 text-[9px] font-bold text-primary-foreground shadow-sm">
                    {items.length}
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      </nav>
    </>
  );
}
